import '../../security.doc'
import React, { useState, memo, useEffect } from 'react'
import PropTypes from 'prop-types'

// components
import CustomCell from './cell/cell.component'
import withOptions from '../../../../../utilityMenu/helper/withOptions'
import UtilityMenu from '../../../../../utilityMenu/utilityMenu.component'
import { AgGrid, Spinner, NoContentMessage } from '../../../../../index'
import { getLocalizedCurrency } from '../../../../../../utils/currencyLocalization'

// utils
import {
  format,
  formatDate,
  getDefaultColumnDef,
  preventDefaultRowClick,
  inMillions,
  isYetToFile,
  ENTITY_TYPE,
  THEMES,
  YET_TO_FILE,
  getLabelByValue,
  HOLDER_TYPE
} from '../../../../../../utils'
import { capitalize, get } from 'lodash'

const Utility = withOptions(UtilityMenu)

const propTypes = {
  pageSizeId: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  total: PropTypes.number,
  listOptions: PropTypes.shape({
    page: PropTypes.number.isRequired,
    limit: PropTypes.number.isRequired
  }).isRequired,
  onQueryChange: PropTypes.func.isRequired,
  holderType: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string.isRequired
  })
}

const defaultProps = {
  loading: false,
  data: [],
  total: 0,
  listOptions: {}
}

const { FUND, INSTITUTION } = ENTITY_TYPE
const NULLABLE_VALUES = ['null']
const EMPTY_PLACEHOLDER = '-'
const ZERO = '0.00'

function formatted (value, formatter = format) {
  return (value || value === 0) ? formatter(value, 0) : EMPTY_PLACEHOLDER
}

/**
 * Get columns based on holderType
 * @param holderType
 */
function getColumns (holderType) {
  const isHidden = (holderType === 'insider')
  const source = (holderType === FUND) ? FUND : INSTITUTION

  return [
    {
      field: 'rank',
      headerName: '#',
      type: 'centered',
      minWidth: 72,
      maxWidth: 72,
      pinned: 'left',
      lockPinned: true,
      sortable: false,
      cellRenderer: 'CustomCell'
    },
    {
      field: `${source}Name`,
      headerName: 'Name',
      type: 'text',
      minWidth: 484,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: 'CustomCell'
    },
    {
      field: 'current',
      headerName: 'Pos',
      type: 'number',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value }) => formatted(value)
    },
    {
      field: 'change',
      headerName: 'Chg',
      type: 'change',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value, data }) => {
        if (isYetToFile(get(data, 'reportDate'), get(data, 'change'))) {
          return YET_TO_FILE
        }
        return formatted(value)
      }
    },
    {
      field: 'marketValue',
      headerName: `MKT VAL (MM ${getLocalizedCurrency()})`,
      type: 'number',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value }) => value ? inMillions(value, 2) : EMPTY_PLACEHOLDER
    },
    {
      field: 'marketValueChange',
      headerName: `CHG (MM ${getLocalizedCurrency()})`,
      type: 'change',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value }) => value ? inMillions(value, 2) : EMPTY_PLACEHOLDER
    },
    {
      field: 'percentTSO',
      headerName: '%OS',
      type: 'number',
      minWidth: 100,
      maxWidth: 100,
      valueFormatter: ({ value }) => value ? format(value, 2) : ZERO
    },
    {
      field: 'percentPortfolio',
      headerName: '%PORT',
      type: 'number',
      minWidth: 100,
      maxWidth: 100,
      valueFormatter: ({ value }) => value ? format(value, 2) : ZERO,
      hide: isHidden
    },
    {
      field: `${source}Style`,
      headerName: 'Style',
      type: 'centered',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value, data }) => {
        const { isNested, fundStyle } = (data || {})
        const style = isNested ? fundStyle : value
        return style && !NULLABLE_VALUES.includes(style) ? style : EMPTY_PLACEHOLDER
      },
      hide: isHidden
    },
    {
      field: `${source}Turnover`,
      headerName: 'Turnover',
      type: 'centered',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value, data }) => {
        const { isNested, fundTurnover } = (data || {})
        const turnover = isNested ? fundTurnover : value
        return turnover && !NULLABLE_VALUES.includes(turnover) ? capitalize(turnover) : EMPTY_PLACEHOLDER
      },
      hide: isHidden
    },
    {
      field: `${source}Type`,
      headerName: 'Type',
      type: 'centered',
      minWidth: 200,
      maxWidth: 200,
      valueFormatter: ({ value, data }) => {
        const { isNested, fundType } = (data || {})
        const holderType = getLabelByValue(HOLDER_TYPE, isNested ? fundType : value)
        return holderType && !NULLABLE_VALUES.includes(holderType) ? holderType : EMPTY_PLACEHOLDER
      },
      hide: isHidden
    },
    {
      field: 'institutionTotalAUM',
      headerName: `AUM (MM ${getLocalizedCurrency()})`,
      type: 'number',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value, data }) => {
        const { isNested, fundPortfolioValue } = (data || {})
        return isNested ? formatted(fundPortfolioValue, inMillions) : formatted(value)
      },
      hide: (source !== INSTITUTION)
    },
    {
      field: 'fundPortfolioValue',
      headerName: `AUM (MM ${getLocalizedCurrency()})`,
      type: 'number',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value }) => formatted(value, inMillions),
      hide: (source !== FUND)
    },
    {
      field: `${source}EquityAUM`,
      headerName: `E AUM (MM ${getLocalizedCurrency()})`,
      type: 'number',
      minWidth: 140,
      maxWidth: 140,
      valueFormatter: ({ value, data }) => {
        const { isNested, fundEquityAUM } = (data || {})
        const fundEAUM = isNested ? fundEquityAUM : (source === FUND) ? value : null
        return fundEAUM ? formatted(fundEquityAUM, inMillions) : formatted(value)
      },
      hide: isHidden
    },
    {
      field: 'reportDate',
      headerName: 'As Of',
      type: 'date',
      minWidth: 120,
      maxWidth: 120,
      valueFormatter: ({ value }) => value ? formatDate(value, undefined, true, true) : EMPTY_PLACEHOLDER
    },
    {
      field: `${source}QualityRating`,
      headerName: 'QR',
      type: 'centered',
      minWidth: 80,
      maxWidth: 80,
      valueFormatter: ({ value, data }) => {
        const { isNested, fundQualityRating } = (data || {})
        const qualityRating = isNested ? fundQualityRating : value
        return qualityRating || EMPTY_PLACEHOLDER
      },
      hide: isHidden
    },
    {
      field: 'activity',
      headerName: 'Last Activity',
      type: 'date',
      minWidth: 134,
      maxWidth: 134,
      valueFormatter: ({ data }) => {
        const activity = get(data, 'activityConnection.items[0].start.date', null)
        return activity ? formatDate(activity, undefined, undefined, true) : EMPTY_PLACEHOLDER
      },
      hide: isHidden
    },
    {
      field: 'filingSource',
      headerName: 'Source',
      type: 'centered',
      minWidth: 220,
      maxWidth: 220,
      cellRenderer: 'CustomCell'
    }
  ].filter((column) => !column.hide)
}

/**
 * Get sorted field
 * @param field
 * @returns {string|*}
 */
function getSortField (field) {
  switch (field) {
    case 'activity':
      return 'activityConnection.start'
    default:
      return field
  }
}

/**
 * Current Holder Table Component
 * @typedef { import('./table.doc').CurrentTableProps } CurrentTableProps
 *
 * @param {CurrentTableProps} props
 * @returns {JSX.Element}
 */
function CurrentHolderTable (props) {
  const { pageSizeId, loading, data, total, listOptions, holderType, history, onQueryChange } = props
  const [agGrid, setAgGrid] = useState({})
  const [utility, setUtility] = useState({
    menu: null,
    items: [
      { action: 'CREATE_ACTIVITY' },
      { action: 'TARGET' },
      { action: 'DOWNLOAD_TEARSHEET' },
      { action: 'BRIEFING_BOOK' }
    ]
  })

  const { menu, items } = (utility || {})
  const columns = getColumns(holderType)

  /**
   * Reset columns on holderType change
   */
  useEffect(() => {
    holderType && agGrid.api && agGrid.api.setColumnDefs([])
  }, [agGrid, holderType])

  /**
   * Handle AgGrid onGridReady event
   * @param grid
   * @see {@link: https://github.com/ag-grid/ag-grid/issues/997}
   */
  const handleGridReady = (grid) => {
    setAgGrid(grid)
    setTimeout(() => agGrid, 0)
  }

  /**
   * Handle AgGrid gridSizeChanged event
   * @param type - event type
   */
  const handleGridResize = ({ type }) => {
    if (type === 'gridSizeChanged') {
      setTimeout(() => agGrid.api && agGrid.api.sizeColumnsToFit(), 0)
    }
  }

  /**
   * Handle row click event
   * @param {SecurityTableRowClickEvent} event
   * @returns {void}
   */
  const handleRowClick = ({ fundId, institutionId, holderType, holdingType }) => {
    if (holdingType === 'stakes') return

    const id = (holderType === FUND) ? fundId : institutionId || null
    id && history && history.push(`/${holderType}/${id}`)
  }

  /**
   * Handle page change
   * @param selected
   */
  const handlePageChange = ({ selected }) => {
    onQueryChange({
      listOptions: { ...listOptions, page: selected }
    })
  }

  /**
   * Handle page size change
   * @param selected
   */
  const handlePageSizeChange = ({ selected }) => {
    onQueryChange({
      listOptions: { ...listOptions, limit: selected, page: 1 }
    })
  }

  /**
   * Handle Column Sort
   * @param grid
   */
  const handleSortChange = (grid) => {
    const api = grid && grid.api
    const sortModel = api && api.getSortModel()

    if (!sortModel && !sortModel.length) {
      return
    }

    onQueryChange({
      listOptions: {
        ...listOptions,
        sortBy: getSortField(sortModel[0].colId),
        sortDir: sortModel[0].sort,
        page: 1
      }
    })
  }

  /**
   * Handle utility item click
   * @param event
   * @param entity
   */
  const handleUtilityClick = (event, entity) => {
    event.stopPropagation()

    const { hasDeals } = (entity || {})
    setUtility({
      menu: { entity, anchorEl: event.currentTarget },
      items: items.map((item) => ({ ...item, hide: (hasDeals && item.action === 'TARGET') }))
    })
  }

  /**
   * Render Utility Menu
   */
  const getUtilityMenu = () => {
    const { menu, items } = utility
    const { entity, anchorEl } = (menu || {})

    return entity
      ? <Utility
          theme={THEMES.LIGHT}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => handleUtilityClose()}
          items={items.filter((item) => !item.hide)}
          entityType={entity.entityType}
          entity={entity}
          hasButton
        />
      : null
  }

  /**
   * Handle Utility Close Click
   */
  const handleUtilityClose = () => {
    setUtility({ ...utility, menu: null })
  }

  /**
   * Renders CustomCell Component
   */
  const renderCustomCell = (props) => {
    return <CustomCell {...props} onUtilityClick={handleUtilityClick} />
  }

  /**
   * Returns grid columns
   */
  const getColumnsDefinition = () => {
    return columns.map((column, idx) => {
      const { type, field } = column
      const columnDef = {
        ...getDefaultColumnDef({ columnIndex: ((idx === 1) ? idx + 1 : idx), type }),
        ...column
      }

      if (field === 'change') {
        columnDef.cellClassRules['ag-cell-value--increase'] = ({ value, data }) => isYetToFile(get(data, 'reportDate'), get(data, 'change')) ? false : value > 0
        columnDef.cellClassRules['ag-cell-value--decrease'] = ({ value, data }) => isYetToFile(get(data, 'reportDate'), get(data, 'change')) ? false : value < 0
      }

      return columnDef
    })
  }

  return (
    <div className={`grid_table ${pageSizeId}_table`}>
      {loading && <Spinner mask theme={THEMES.RAIN} />}
      {!data.length
        ? <NoContentMessage />
        : <AgGrid
            key='current-table_loaded-state'
            domLayout='autoHeight'
            sizeToFit
            hideAutoColumn
            // suppress configs
            suppressMovableColumns
            suppressContextMenu
            // columns and data
            defaultColDef={{
              suppressMenu: true,
              sortable: true
            }}
            columnDefs={getColumnsDefinition()}
            rowData={data}
            // pagination
            pagination
            paginationProps={{
              pageSizeId,
              forcePage: listOptions.page,
              initialPageSize: listOptions.limit,
              showPageSizeSelection: true,
              total,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange
            }}
            // custom components
            frameworkComponents={{
              CustomCell: renderCustomCell
            }}
            // hierarchy grouping for expandable rows
            treeData
            groupDefaultExpanded={0}
            getDataPath={(data) => data.hierarchy}
            // event listeners
            onGridReady={handleGridReady}
            onGridSizeChanged={handleGridResize}
            onSortChanged={handleSortChange}
            onRowClicked={preventDefaultRowClick(
              handleRowClick, ['cell_indicator--utility']
            )}
            rowClassRules={{ 'ag-row--expanded': ({ data, node }) => data.isNested || node.expanded }}
            isPinned={data.length}
          />}
      {menu && getUtilityMenu()}
    </div>
  )
}

CurrentHolderTable.propTypes = propTypes
CurrentHolderTable.defaultProps = defaultProps

export default memo(CurrentHolderTable)
